import { Star, ArrowsClockwise, MagnifyingGlass, CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight, GoogleLogo, MicrosoftOutlookLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { t } from "@/lib/i18n";
import dayjs from "dayjs";
import { toast } from "sonner";
import api, { errDetail } from "@/lib/api";

const connectProvider = async (provider) => {
  if (provider === "gmail") {
    try {
      const { data } = await api.get("/oauth/gmail/start");
      window.location.href = data.url;
    } catch (e) { toast.error(errDetail(e)); }
    return;
  }
  try {
    const { data } = await api.post("/connections/connect", { provider });
    data.configured ? toast.success(data.message) : toast.info(data.message, { duration: 8000 });
  } catch (e) { toast.error(errDetail(e)); }
};

export const EmailList = ({ folder, folderLabel, threads, total, page, setPage, search, setSearch, onRefresh, selectedThread, onSelect, onToggleStar, lastSynced, wide = false, categories = [], catCounts = {}, catFilter = "", setCatFilter = () => {}, globalSearch = null, replyingMap = {} }) => {
  const perPage = 25;
  const maxPage = Math.max(1, Math.ceil(total / perPage));
  const label = folderLabel || folder.charAt(0).toUpperCase() + folder.slice(1);
  const catById = Object.fromEntries(categories.map((c) => [c.id, c]));

  return (
    <div className={`${wide ? "flex-1 min-w-0" : "w-[380px] shrink-0"} h-full border-r border-gray-200 flex flex-col bg-white transition-all`} data-testid="email-list-container">
      <div className="p-3 border-b border-gray-200">
        {globalSearch || (
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="email-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t("list.search_ph", "Search emails... (try from:name, is:unread)")}
            className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        )}
        {categories.length > 0 && (
          <div data-testid="category-filter-bar" className="flex items-center gap-1.5 mt-2 overflow-x-auto scroll-thin pb-0.5">
            <button data-testid="cat-filter-all" onClick={() => setCatFilter("")}
              className={`text-[11px] font-semibold rounded-full px-2.5 py-1 shrink-0 transition-colors ${!catFilter ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
              {t("cats.all", "All")}
            </button>
            <button data-testid="cat-filter-vip" onClick={() => setCatFilter(catFilter === "vip" ? "" : "vip")}
              className={`text-[11px] font-semibold rounded-full px-2.5 py-1 shrink-0 transition-colors ${catFilter === "vip" ? "bg-amber-400 text-white" : "bg-amber-50 text-amber-600 hover:bg-amber-100"}`}>
              ★ VIP
            </button>
            {categories.map((c) => (
              <button key={c.id} data-testid={`cat-filter-${c.id}`} onClick={() => setCatFilter(catFilter === c.id ? "" : c.id)}
                className={`flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 shrink-0 transition-colors ${catFilter === c.id ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                style={catFilter === c.id ? { backgroundColor: c.color } : {}}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: catFilter === c.id ? "#fff" : c.color }} />
                {c.name}{catCounts[c.id] ? ` ${catCounts[c.id]}` : ""}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
        <div className="flex items-baseline gap-2">
          <h2 data-testid="folder-title" className="font-heading font-bold text-gray-900">{label}</h2>
          <span data-testid="conversation-count" className="text-xs text-gray-400">{total} conversations</span>
        </div>
        <div className="flex items-center gap-2">
          {lastSynced && <span data-testid="last-synced-label" className="text-[11px] text-gray-400">Synced {dayjs(lastSynced).format("h:mm A")}</span>}
          <button data-testid="refresh-button" onClick={onRefresh} className="text-gray-400 hover:text-gray-700 transition-colors p-1">
            <ArrowsClockwise size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin">
        {threads.length === 0 && (
          folder === "inbox" && !search ? (
            <div data-testid="connect-empty-state" className="p-6 text-center">
              <EnvelopeSimple size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm font-semibold text-gray-700 mb-1">{t("inbox.ready", "Your inbox is ready")}</p>
              <p className="text-xs text-gray-400 mb-4">{t("inbox.connect_sub", "Connect your email account to start managing real mail with your agent.")}</p>
              <div className="space-y-2">
                <button data-testid="empty-connect-gmail" onClick={() => connectProvider("gmail")}
                  className="w-full flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-sm font-medium rounded-md py-2 transition-colors">
                  <GoogleLogo size={16} weight="bold" className="text-red-500" /> Connect Gmail
                </button>
                <button data-testid="empty-connect-outlook" onClick={() => connectProvider("outlook")}
                  className="w-full flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-sm font-medium rounded-md py-2 transition-colors">
                  <MicrosoftOutlookLogo size={16} weight="bold" className="text-blue-500" /> Connect Outlook
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="empty-folder-message" className="text-sm text-gray-400 text-center py-16">{t("inbox.no_convos", "No conversations here")}</div>
          )
        )}
        {threads.map((t) => {
          const m = t.latest;
          const active = selectedThread === t.thread_id;
          return (
            <button key={t.thread_id} data-testid={`email-row-${t.thread_id}`} onClick={() => onSelect(t.thread_id)}
              className={`w-full text-left px-3 py-3 border-b border-gray-100 flex gap-2 transition-colors ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}>
              <div className="flex flex-col items-center gap-1.5 pt-0.5 w-5 shrink-0">
                {t.unread ? <span className="w-2 h-2 rounded-full bg-blue-600" /> : <span className="w-2 h-2" />}
                <span data-testid={`star-toggle-${t.thread_id}`} role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onToggleStar(t); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onToggleStar(t); } }}
                  className={`transition-colors ${t.starred ? "text-amber-400" : "text-gray-300 hover:text-gray-500"}`}>
                  <Star size={15} weight={t.starred ? "fill" : "regular"} />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`text-sm truncate ${t.unread ? "font-semibold text-gray-900" : "text-gray-700"}`}>{m.from_name}</span>
                  {t.count > 1 && <span className="text-[10px] bg-gray-200 text-gray-600 rounded-full px-1.5 py-px shrink-0">{t.count}</span>}
                  {replyingMap[t.thread_id]?.length > 0 && (
                    <span data-testid={`si-replying-badge-${t.thread_id}`} title={`${replyingMap[t.thread_id].join(", ")} is replying`}
                      className="text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full px-1.5 py-px shrink-0">✍ {replyingMap[t.thread_id][0]}</span>
                  )}
                  <span className={`ml-auto text-[11px] shrink-0 ${m.send_at ? "text-amber-600 font-semibold" : "text-gray-400"}`}>
                    {m.send_at ? `⏰ ${dayjs(m.send_at).format("MMM D, H:mm")}` : dayjs(m.created_at).format("h:mm A")}
                  </span>
                </div>
                <div className={`text-[13px] truncate ${t.unread ? "text-gray-800" : "text-gray-500"}`}>{m.subject}</div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400 truncate">{m.snippet}</span>
                  {catById[m.category_id] && (
                    <span data-testid={`email-cat-chip-${t.thread_id}`} className="ml-auto shrink-0 flex items-center gap-1 text-[10px] font-semibold rounded-full px-1.5 py-px"
                      style={{ backgroundColor: `${catById[m.category_id].color}1a`, color: catById[m.category_id].color }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: catById[m.category_id].color }} />
                      {catById[m.category_id].name}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 text-xs text-gray-500">
        <span data-testid="pagination-info">Showing {total === 0 ? 0 : (page - 1) * perPage + 1}-{Math.min(page * perPage, total)} of {total}</span>
        <div className="flex items-center gap-1">
          <button data-testid="page-first" disabled={page <= 1} onClick={() => setPage(1)} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><CaretDoubleLeft size={13} /></button>
          <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><CaretLeft size={13} /></button>
          <span className="border border-gray-200 rounded px-2 py-0.5">{page}</span>
          <button data-testid="page-next" disabled={page >= maxPage} onClick={() => setPage(page + 1)} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><CaretRight size={13} /></button>
          <button data-testid="page-last" disabled={page >= maxPage} onClick={() => setPage(maxPage)} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><CaretDoubleRight size={13} /></button>
        </div>
      </div>
    </div>
  );
};
